import React, { useState, useEffect } from 'react';
import { interviewersAPI } from '../../utils/api';
import './InterviewerStats.css';

function InterviewerStats({ user }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await interviewersAPI.getStats();
      setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to fetch interviewer stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="interviewer-stats">
        <div className="loading-spinner">Loading stats...</div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="interviewer-stats">
        <div className="empty-state">No stats available</div>
      </div>
    );
  }

  // Get CSS variable values for colors
  const getCSSVariable = (varName, fallback) => {
    if (typeof window !== 'undefined') {
      const value = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
      return value || fallback;
    }
    return fallback;
  };

  const successColor = getCSSVariable('--success', '#28a745');
  const warningColor = getCSSVariable('--warning', '#ffc107');
  const dangerColor = getCSSVariable('--danger', '#dc3545');
  const infoColor = getCSSVariable('--info', '#007bff');
  const primaryColor = getCSSVariable('--primary-600', '#1e88e5'); // eslint-disable-line no-unused-vars
  const statusInProgressColor = getCSSVariable('--status-in-progress-text', '#a855f7');

  const completionRateColor = stats.evaluations.completionRate >= 90
    ? successColor
    : stats.evaluations.completionRate >= 70
    ? warningColor
    : dangerColor;

  const qualityScoreColor = stats.performance.qualityScore === 'Excellent'
    ? successColor
    : stats.performance.qualityScore === 'Good'
    ? infoColor
    : stats.performance.qualityScore === 'Fair'
    ? warningColor
    : dangerColor;

  return (
    <div className="interviewer-stats">
      {/* Overview Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 3v18h18" />
              <path d="M7 12l4-4 4 4 6-6v8H7z" />
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.overview.totalEvaluations}</div>
            <div className="stat-label">Total Evaluations</div>
            <div className="stat-sublabel">
              {stats.evaluations.thisMonth} this month
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-value" style={{ color: completionRateColor }}>
              {stats.evaluations.completionRate}%
            </div>
            <div className="stat-label">Completion Rate</div>
            <div className="stat-sublabel">
              {stats.evaluations.total} / {stats.overview.totalAssessments} completed
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.performance.avgResponseTimeHours.toFixed(1)}h</div>
            <div className="stat-label">Avg Response Time</div>
            <div className="stat-sublabel">
              Time to evaluate after completion
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-value" style={{ color: qualityScoreColor }}>
              {stats.performance.qualityScore}
            </div>
            <div className="stat-label">Evaluation Quality</div>
            <div className="stat-sublabel">
              Avg note length: {stats.performance.avgNoteLength} chars
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2L2 7l10 5 10-5-10-5z" />
              <path d="M2 17l10 5 10-5" />
              <path d="M2 12l10 5 10-5" />
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-value" style={{ color: stats.performance.efficiencyScore >= 80 ? successColor : stats.performance.efficiencyScore >= 60 ? warningColor : dangerColor }}>
              {stats.performance.efficiencyScore || 0}
            </div>
            <div className="stat-label">Efficiency Score</div>
            <div className="stat-sublabel">
              Based on completion, speed, and quality
            </div>
          </div>
        </div>
      </div>

      {/* Average Scores */}
      <div className="stats-section">
        <h3>Average Evaluation Scores</h3>
        <div className="scores-grid">
          <div className="score-card">
            <div className="score-label">Technical</div>
            <div className="score-value">{stats.scores.avgTechnical.toFixed(1)}</div>
            <div className="score-bar">
              <div
                className="score-bar-fill"
                style={{
                  width: `${(stats.scores.avgTechnical / 10) * 100}%`,
                  backgroundColor: infoColor
                }}
              />
            </div>
          </div>

          <div className="score-card">
            <div className="score-label">Problem Solving</div>
            <div className="score-value">{stats.scores.avgProblemSolving.toFixed(1)}</div>
            <div className="score-bar">
              <div
                className="score-bar-fill"
                style={{
                  width: `${(stats.scores.avgProblemSolving / 10) * 100}%`,
                  backgroundColor: statusInProgressColor
                }}
              />
            </div>
          </div>

          <div className="score-card">
            <div className="score-label">Communication</div>
            <div className="score-value">{stats.scores.avgCommunication.toFixed(1)}</div>
            <div className="score-bar">
              <div
                className="score-bar-fill"
                style={{
                  width: `${(stats.scores.avgCommunication / 10) * 100}%`,
                  backgroundColor: successColor
                }}
              />
            </div>
          </div>

          <div className="score-card">
            <div className="score-label">Overall</div>
            <div className="score-value">{stats.scores.avgOverall.toFixed(1)}</div>
            <div className="score-bar">
              <div
                className="score-bar-fill"
                style={{
                  width: `${(stats.scores.avgOverall / 10) * 100}%`,
                  backgroundColor: warningColor
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Recommendation Distribution */}
      <div className="stats-section">
        <h3>Recommendation Distribution</h3>
        <div className="recommendations-grid">
          <div className="recommendation-item">
            <div className="recommendation-label">Strong Hire</div>
            <div className="recommendation-value">{stats.recommendations.strong_hire}</div>
          </div>
          <div className="recommendation-item">
            <div className="recommendation-label">Hire</div>
            <div className="recommendation-value">{stats.recommendations.hire}</div>
          </div>
          <div className="recommendation-item">
            <div className="recommendation-label">No Hire</div>
            <div className="recommendation-value">{stats.recommendations.no_hire}</div>
          </div>
          <div className="recommendation-item">
            <div className="recommendation-label">Strong No Hire</div>
            <div className="recommendation-value">{stats.recommendations.strong_no_hire}</div>
          </div>
        </div>
      </div>

      {/* Interview Stats */}
      <div className="stats-section">
        <h3>Interview Statistics</h3>
        <div className="interview-stats-grid">
          <div className="interview-stat-item">
            <div className="interview-stat-label">Total Interviews</div>
            <div className="interview-stat-value">{stats.interviews.total}</div>
          </div>
          <div className="interview-stat-item">
            <div className="interview-stat-label">This Week</div>
            <div className="interview-stat-value">{stats.interviews.thisWeek}</div>
          </div>
          <div className="interview-stat-item">
            <div className="interview-stat-label">This Month</div>
            <div className="interview-stat-value">{stats.interviews.thisMonth}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InterviewerStats;

